import { firestoreService } from './firestore';
import { auth, db } from './firebase';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { buildAccessToken, hashPassword, normaliseEmail } from '../utils/accessCredentials';
import { normalisePhoneNumber } from '../utils/phoneNumbers';

const buildAccessShareId = async (linkId, recipientEmail) => {
  if (!linkId || !recipientEmail) return '';
  return `access-${await hashPassword(`${linkId}:${recipientEmail}`)}`;
};

export const shareService = {
  listShares() {
    return firestoreService
      .list('sharabl-shares', [where('ownerId', '==', auth.currentUser?.uid || '')])
      .then((shares) => shares.filter((share) => !share.hiddenFromDashboard));
  },

  listSharesForLink(linkId) {
    return this.listShares().then((shares) => shares.filter((share) => share.linkId === linkId));
  },

  async ensureAccessIndex(share) {
    if (!share?.linkId || !share?.recipientEmail || !share?.passwordHash || !share?.publicAccess) {
      return null;
    }

    const accessId = await buildAccessShareId(share.linkId, share.recipientEmail);
    if (!accessId || share.id === accessId) return null;

    const accessRecord = {
      recipient: share.recipientEmail,
      recipientEmail: share.recipientEmail,
      channel: share.channel || 'Email',
      access: share.access || 'Email + password',
      status: 'Access index',
      linkId: share.linkId,
      linkTitle: share.linkTitle || '',
      shareUrl: share.shareUrl || '',
      phone: normalisePhoneNumber(share.phone),
      hasPassword: true,
      password: '',
      passwordHash: share.passwordHash,
      accessToken: share.accessToken || '',
      publicAccess: true,
      ownerId: share.ownerId || auth.currentUser?.uid || '',
      requestId: share.id,
      hiddenFromDashboard: true,
      updatedAt: serverTimestamp(),
    };

    await setDoc(doc(db, 'sharabl-shares', accessId), {
      ...accessRecord,
      createdAt: share.createdAt || serverTimestamp(),
    }, { merge: true });

    return { ...accessRecord, id: accessId };
  },

  async createShare(data) {
    const password = data.password || '';
    const hasPassword = Boolean(password.trim());
    const phone = normalisePhoneNumber(data.phone);
    const allowedEmails = (data.allowedEmails?.length ? data.allowedEmails : [data.recipient])
      .map(normaliseEmail)
      .filter(Boolean);
    const recipientEmail = allowedEmails[0] || normaliseEmail(data.recipient);
    const passwordHash = hasPassword ? await hashPassword(password) : '';

    if (hasPassword && allowedEmails.length > 1) {
      return Promise.all(allowedEmails.map(async (email) => {
        const id = await buildAccessShareId(data.linkId, email);

        return firestoreService.create('sharabl-shares', {
          id,
          recipient: email,
          recipientEmail: email,
          channel: data.channel || 'Email',
          access: data.access || 'Email + password',
          status: data.status || 'Access created',
          linkId: data.linkId,
          shareUrl: data.shareUrl || '',
          phone,
          hasPassword,
          password: '',
          passwordHash,
          accessToken: await buildAccessToken(data.linkId, email, password),
          publicAccess: true,
          ownerId: data.ownerId || auth.currentUser?.uid || '',
        }, 'share');
      }));
    }

    const id = hasPassword ? await buildAccessShareId(data.linkId, recipientEmail) : undefined;

    const payload = {
      recipient: data.recipient || 'New recipient',
      recipientEmail,
      channel: data.channel || 'Copy link',
      access: data.access || 'Invite only',
      status: data.status || 'Sent',
      linkId: data.linkId,
      shareUrl: data.shareUrl || '',
      phone,
      hasPassword,
      password: '',
      passwordHash,
      accessToken: hasPassword ? await buildAccessToken(data.linkId, recipientEmail, password) : '',
      publicAccess: hasPassword,
      ownerId: data.ownerId || auth.currentUser?.uid || '',
    };

    return firestoreService.create('sharabl-shares', id ? { ...payload, id } : payload, 'share');
  },

  updateShare(id, data) {
    return firestoreService.update('sharabl-shares', id, data);
  },

  async grantInviteRequest(id, data) {
    if (!id) {
      throw new Error('Choose an invite request before sending access.');
    }

    const password = data.password || '';
    const hasPassword = Boolean(password.trim());
    const allowedEmails = (data.allowedEmails?.length ? data.allowedEmails : [data.recipient])
      .map(normaliseEmail)
      .filter(Boolean);
    const recipientEmail = allowedEmails[0] || normaliseEmail(data.recipient);

    if (!data.linkId || !recipientEmail || !hasPassword) {
      throw new Error('Invite access needs a link, recipient email, and password.');
    }

    const passwordHash = await hashPassword(password);
    const phone = normalisePhoneNumber(data.phone);
    const accessId = await buildAccessShareId(data.linkId, recipientEmail);
    const accessRecord = {
      recipient: recipientEmail,
      recipientEmail,
      channel: data.channel || 'Email',
      access: data.access || 'Email + password',
      status: data.status || 'Access created',
      linkId: data.linkId,
      shareUrl: data.shareUrl || '',
      ...(phone ? { phone } : {}),
      hasPassword,
      password: '',
      passwordHash,
      accessToken: await buildAccessToken(data.linkId, recipientEmail, password),
      publicAccess: true,
      ownerId: auth.currentUser?.uid || '',
    };
    const updatedRequest = await firestoreService.update('sharabl-shares', id, {
      ...accessRecord,
      respondedAt: new Date().toISOString(),
    });

    if (accessId && accessId !== id) {
      await firestoreService.create('sharabl-shares', {
        ...accessRecord,
        id: accessId,
        requestId: id,
        status: 'Access index',
        hiddenFromDashboard: true,
      }, 'share');
    }

    return updatedRequest;
  },

  async createInviteRequest(link, request = {}) {
    if (!link?.id) {
      throw new Error('Invite requests need a valid share link.');
    }

    const recipientEmail = normaliseEmail(request.email);
    const phone = normalisePhoneNumber(request.phone);
    const reason = String(request.reason || '').trim().slice(0, 160);
    const isWaitingList = link.inviteRequestMode === 'waiting-list';

    if (!recipientEmail && !phone) {
      throw new Error('Enter an email address or mobile number.');
    }

    const now = new Date().toISOString();
    const payload = {
      type: 'invite-request',
      recipient: recipientEmail || phone,
      recipientEmail,
      phone,
      reason,
      channel: isWaitingList ? 'Waiting list' : 'Invite request',
      access: isWaitingList ? 'Waiting list' : 'Invite request',
      status: isWaitingList ? 'Waiting list' : 'Invite requested',
      inviteRequestMode: link.inviteRequestMode || 'request',
      linkId: link.id,
      linkTitle: link.title || '',
      shareUrl: '',
      hasPassword: false,
      password: '',
      passwordHash: '',
      accessToken: '',
      publicAccess: false,
      ownerId: link.ownerId || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const created = await addDoc(collection(db, 'sharabl-shares'), payload);
    return { ...payload, id: created.id, createdAt: now, updatedAt: now };
  },

  async verifyShareAccess(linkId, email, password) {
    const accessRecord = await this.verifyShareAccessRecord(linkId, email, password);
    return accessRecord?.accessToken || '';
  },

  async verifyShareAccessRecord(linkId, email, password) {
    const recipientEmail = normaliseEmail(email);
    const passwordHash = await hashPassword(password);
    const shareId = await buildAccessShareId(linkId, recipientEmail);

    if (!linkId || !recipientEmail || !passwordHash || !shareId) return null;

    const snapshot = await getDoc(doc(db, 'sharabl-shares', shareId));
    if (snapshot.exists()) {
      const data = snapshot.data();
      const hasAccess = data.linkId === linkId &&
        data.recipientEmail === recipientEmail &&
        data.passwordHash === passwordHash &&
        data.publicAccess === true;

      return hasAccess
        ? {
          id: snapshot.id,
          ...data,
          accessToken: data.accessToken || await buildAccessToken(linkId, recipientEmail, password),
        }
        : null;
    }

    return null;
  },
};
